import { apiErrorField } from '@agos/api-client'
import {
  useDeleteCustomerAuthMeHiddenAddresses,
  useGetCustomerAuthMe,
  useGetCustomerAuthMeReferrals,
  useGetCustomerAuthMeSavedAddresses,
  useGetCustomerAuthMyCoupons,
  useGetCustomerAuthMyOrders,
  useGetCustomerReferral,
  useGetLoyaltyPublicInfo,
  usePatchCustomerAuthMe,
  usePatchCustomerAuthMeAddressLabel,
  usePatchCustomerAuthMePassword,
  usePostCustomerAuthMeHiddenAddresses,
  useDeleteCustomerAuthMeRequestDeletion,
  usePostCustomerAuthMeRequestDeletion,
  usePostCustomerAuthMeSavedAddresses,
  usePostCustomerAuthMyOrdersOrderIdCancel,
} from '@agos/api-client/customers'
import { useGetMiniGameStatus, usePostMiniGamePlay } from '@agos/api-client/marketing'
import {
  getCustomerAuthMyOrdersOrderId,
  useGetCustomerAuthMyOrdersOrderIdTracking,
  useGetPushPublicKey,
} from '@agos/api-client/customers'
import { getProducts } from '@agos/api-client/menu'
import { T, formatCents, formatCount, tt } from '@agos/shared'
import { ApiError } from '@agos/api-client'
import { cn, toast } from '@agos/ui'
import { CheckCheck, Copy, Loader2, Lock, LogOut, MapPin, Pencil, Plus, Share2, ShoppingBag, Star, User } from 'lucide-react'
import { useMemo, useState } from 'react'

import { ProfileSection } from './ProfileSection'
import { Redirect, useLocation } from 'wouter'

import { NotificationPreferences } from './NotificationPreferences'
import { OrderHistoryCard, type OrderDetail } from './OrderHistoryCard'
import { SavedAddressRow } from './SavedAddressRow'
import {
  deletionConsequences,
  deletionRequestedNote,
  deletionStep,
  withdrawalNote,
} from './deletion'
import { reorderFrom, reorderNote, type PastOrderItem } from './reorder'
import { emptyTimelineNote, stepNote, trackingInterval, trackingOf } from './tracking'
import { PullToRefresh } from './PullToRefresh'

import {
  emptyNote,
  minimumNote,
  COUPON_FILTERS,
  couponFilterCount,
  couponFilterLabel,
  couponsMatching,
  myCouponsOf,
  usable,
  type CouponFilter,
  sourceNote,
  statusBadge,
  statusIsGood,
  worthNote,
} from './my-coupons'
import {
  gameEmoji,
  gameNote,
  gameTitle,
  mayPlay,
  playPayload,
  prizeCoupon,
  prizeEmoji,
  prizeHeadline,
  prizePointsNote,
  prizeFrom,
  prizeNote,
  refusedUntil,
  runningGames,
  shouldSaySpent,
  statusOf,
  type GameKey,
} from './minigame'
import { ScratchCard } from './ScratchCard'
import {
  defaultAddressPayload, addressesOf, alreadyKnown, balanceNote, levelProgress, levelNote, maxRedeemable, ratesOf, redemptionNote, personalDiscountAmount, tierFor, tierName, useCart, useCustomerAuth, usePush, type SavedAddress } from '@agos/customer-core'
import {
  hasReferrals,
  referralActivityOf,
  referralDiscountLabel,
  referralOfferOf,
  shareText,
  referralDate,
  referralName,
  referralNote,
} from './referrals'
import {
  ORDERS_PAGE_SIZE,
  cancelNote,
  cancelConfirmation,
  cancelFailed,
  cancelSucceeded,
  mayCancel,
  nextLimit,
  orderItemsOf,
  orderTotalsOf,
  ordersOf,
  statusLabel,
} from './orders'

/**
 * The customer's own account.
 *
 * Legacy's `MojProfil.tsx` is 2 223 lines and reaches 15 endpoints. The rules live in `loyalty.ts`,
 * `orders.ts` and `addresses.ts` beside this file; here is the wiring.
 *
 * **What this screen refuses to imply.** The tier is the order count, not the points — a bronze
 * customer holding 937 points is ordinary on this data. Points below the redemption minimum are named
 * as unusable rather than shown as a balance, because 846 of 2 465 customers are in exactly that
 * position and would otherwise find out at the checkout. And cancelling is offered on `new` alone,
 * which is 4 rows of 10 491, because the server answers 409 for everything else.
 */
export function ProfilePage() {
  const signOut = useCustomerAuth((state) => state.signOut)

  // The session as the store knows it, which is the same thing legacy's auth context knows: whether
  // there is a token at all. Asking the server first costs a 401 and three retries before the page can
  // admit nobody is signed in — see the gate below.
  const sessionUser = useCustomerAuth((state) => state.user)
  // No session, nothing to ask — of any of them. And a token the server rejects is rejected: retrying a
  // 401 three times only delays the redirect. Shared so a query added later is guarded by default.
  const session = { enabled: sessionUser !== null, retry: false } as const

  /**
   * Which of the heavy sections are open — §9.21.
   *
   * This page opened twelve queries on mount, four of them for sections a customer looks at least
   * often. A shut section does not ask: `openSections` feeds each query's `enabled`, so opening one is
   * what fetches it. The three a customer came for — who they are, their loyalty standing, their
   * addresses — are not in here and load as before.
   */
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const isOpen = (key: string) => openSections[key] === true
  const toggle = (key: string) => setOpenSections((open) => ({ ...open, [key]: !open[key] }))
  const deferred = (key: string) =>
    ({ enabled: sessionUser !== null && isOpen(key), retry: false }) as const
  const account = useGetCustomerAuthMe({ query: session })
  const loyalty = useGetLoyaltyPublicInfo()
  const saved = useGetCustomerAuthMeSavedAddresses({ query: session })
  // Legacy pages the history twenty at a time and offers *Načítať ďalších 20*. This asked for one page
  // and offered nothing, so a customer past twenty orders could not reach the rest — measured on this
  // data: one has **82**, and **24** are past a single page. `nextOffset` was written and tested and
  // called by nobody, which is the ninth time in this port (FB-C57).
  const [orderLimit, setOrderLimit] = useState(ORDERS_PAGE_SIZE)
  const history = useGetCustomerAuthMyOrders(
    { limit: orderLimit, offset: 0 },
    { query: deferred('orders') },
  )
  // Paged like the history, and for the same reason: a customer who has referred a lot of people
  // should be able to see them all.
  const [referralLimit, setReferralLimit] = useState(ORDERS_PAGE_SIZE)
  // This customer's own code, which is a different endpoint from the list of people they referred.
  const referralOffer = useGetCustomerReferral({ query: session })
  const referrals = useGetCustomerAuthMeReferrals(
    { limit: referralLimit, offset: 0 },
    { query: deferred('referrals') },
  )
  const myCoupons = useGetCustomerAuthMyCoupons({ query: deferred('coupons') })
  // §9.246 — the games are cards now rather than a shut section, so the query is no longer
  // deferred: a card that is always on screen has to have asked.
  const games = useGetMiniGameStatus({ query: session })
  // The VAPID key decides whether push can deliver at all — FB-C03. Same query the banner reads, so
  // this is the cache and not a second request.
  const pushKey = useGetPushPublicKey()
  // …and this is the browser's half, which the page used to hand down as a literal `false` — FB-397.
  // 114 subscriptions across 104 customers were told push was off.
  const push = usePush()
  const playGame = usePostMiniGamePlay()
  // §9.271 — the whole result, not just a caption. Every field below was already in the play response
  // and already parsed; three of the four were being dropped at the point of display.
  const [prize, setPrize] = useState<{
    game: string
    note: string
    headline: string
    emoji: string
    coupon: string | null
    points: string
    won: boolean
  } | null>(null)
  const [, navigate] = useLocation()
  // The catalogue is **not** fetched here. It is 140 KB — the heaviest thing this page could ask for —
  // and its only reader is `again()` below, which lives in the orders section that §9.21 made lazy. A
  // hook would have re-eagered the one request the section was collapsed to avoid. `again()` fetches
  // it the same way it already fetches the order's own detail: on the click that needs it.
  const [reordering, setReordering] = useState<number | null>(null)
  const [reorderProblem, setReorderProblem] = useState('')
  const [tracked, setTracked] = useState<number | null>(null)
  /**
   * Which order cards are open, and what each has fetched — §9.247.
   *
   * Absent means shut and unasked. The detail arrives on the click that opens the card, exactly as
   * `again()` fetches the same order to rebuild a basket; §9.21 made this section lazy to keep the
   * catalogue off the page, and twenty eager detail fetches would undo it.
   */
  const [openOrders, setOpenOrders] = useState<Record<number, OrderDetail>>({})

  async function toggleOrder(orderId: number) {
    if (openOrders[orderId] !== undefined) {
      setOpenOrders((open) => {
        const next = { ...open }
        delete next[orderId]
        return next
      })
      return
    }
    setOpenOrders((open) => ({ ...open, [orderId]: { kind: 'loading' } }))
    try {
      const detail = await getCustomerAuthMyOrdersOrderId(orderId)
      setOpenOrders((open) => ({
        ...open,
        [orderId]: { kind: 'loaded', items: orderItemsOf(detail), totals: orderTotalsOf(detail) },
      }))
    } catch {
      // Said rather than left as an empty card: *nothing happened* is the one outcome a customer
      // cannot act on.
      setOpenOrders((open) => ({ ...open, [orderId]: { kind: 'error' } }))
    }
  }
  const addLine = useCart((state) => state.add)

  /**
   * Where the open order has got to, asked every 15 seconds until it stops moving.
   *
   * §14.1: legacy's interval, copied rather than judged. The poll is driven by the answer — a delivered
   * order stops it — so a page left open on a finished order stops waking the phone.
   */
  const trackingQuery = useGetCustomerAuthMyOrdersOrderIdTracking(tracked ?? 0, {
    query: {
      enabled: tracked !== null,
      retry: false,
      refetchInterval: (query) => trackingInterval(trackingOf(query.state.data)),
    },
  })
  const tracking = trackingOf(trackingQuery.data)

  const patchMe = usePatchCustomerAuthMe()
  const changePassword = usePatchCustomerAuthMePassword()
  const saveAddress = usePostCustomerAuthMeSavedAddresses()
  const hideAddress = usePostCustomerAuthMeHiddenAddresses()
  const unhideAddress = useDeleteCustomerAuthMeHiddenAddresses()
  const labelAddress = usePatchCustomerAuthMeAddressLabel()
  const cancelOrder = usePostCustomerAuthMyOrdersOrderIdCancel()
  const requestDeletion = usePostCustomerAuthMeRequestDeletion()
  // §9.240 — served since the port, called by nothing until now.
  const withdrawDeletion = useDeleteCustomerAuthMeRequestDeletion()
  const [confirmingDeletion, setConfirmingDeletion] = useState(false)
  // §9.396 - the order a customer is being asked about, and what the server said about the last one.
  // Legacy asks before cancelling and says what happened after; the port did neither.
  const [confirmingCancel, setConfirmingCancel] = useState<number | null>(null)
  const [cancelNotice, setCancelNotice] = useState('')

  // `as { user?: Record<string, unknown> }` until 2026-08-03: `/customer-auth/me` describes
  // itself now (BLOCKED #4), so this is `CustomerProfile` and the guards below could go.
  const user = account.data?.user
  // The server's answer wins over the local step — a request on file is a request on file.
  const deletionAt = deletionStep(user?.deletionRequestedAt, confirmingDeletion)
  const rates = useMemo(() => ratesOf(loyalty.data), [loyalty.data])
  const addresses = useMemo(() => addressesOf(saved.data), [saved.data])
  const orders = useMemo(() => ordersOf(history.data), [history.data])
  const referralActivity = useMemo(() => referralActivityOf(referrals.data), [referrals.data])
  const offer = useMemo(() => referralOfferOf(referralOffer.data), [referralOffer.data])
  const coupons = useMemo(() => myCouponsOf(myCoupons.data), [myCoupons.data])
  // §9.272 — *Platné* first, as legacy opens on.
  const [couponFilter, setCouponFilter] = useState<CouponFilter>('active')
  const shownCoupons = useMemo(
    () => couponsMatching(coupons, couponFilter),
    [coupons, couponFilter],
  )
  // §9.264, owner O6 — only the games that are running. Legacy says the same thing in one line,
  // twice: `if (!status?.isActive) return null`.
  const gameStates = useMemo(() => runningGames(statusOf(games.data)), [games.data])

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  // §9.226 — legacy shows the account and offers to edit it; this used to be an open form.
  const [editingAccount, setEditingAccount] = useState(false)
  // §9.228 — legacy gates its *Nová adresa* form on this, opened from the header or the empty state.
  const [addingAddress, setAddingAddress] = useState(false)
  // §9.248 — which address has its name open, not whether any has.
  const [labelEditing, setLabelEditing] = useState<string | null>(null)
  // §9.231 — legacy's *Zmeniť heslo* link; the fields did not exist until it is pressed.
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [labelDraft, setLabelDraft] = useState<Record<string, string>>({})
  const [newAddress, setNewAddress] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [notice, setNotice] = useState('')
  const [copiedCode, setCopiedCode] = useState(false)

  // The `typeof` guards under these were for an untyped payload. `/customer-auth/me` describes
  // itself now (BLOCKED #4) and all three columns are NOT NULL, so the only case left is `user`
  // being absent while the request is in flight — which is what `??` is for.
  const points = user?.pointsBalance ?? 0
  const orderCount = user?.orderCount ?? 0
  const level = user?.loyaltyLevel ?? 'bronze'
  // §9.246 — the tier as the franchise writes it, falling back to legacy's five rather than
  // to the database key the badge used to print.
  const tier = tierFor(loyalty.data, level)
  const progress = levelProgress(rates, orderCount)

  // Prefilled from the server once, and not re-synced on every render — a field that resets under the
  // cursor while it is being typed in is worse than one that starts empty.
  // `name`, `phone` and `email` are nullable columns — a customer taken at the till from a phone
  // number alone has no name — so these coalesce rather than guard a type nobody can send now.
  const displayName = user?.name ?? ''
  const displayPhone = user?.phone ?? ''

  // Legacy's own two states, and the order matters: a spinner while the session is being checked, and
  // a redirect once it is known there is none. An AGOS draft showed a *please sign in* panel instead,
  // which leaves the customer on a page with a sign-in link they then have to find.
  //
  // The store answers first. Legacy's `isLoading` comes from its auth context, which knows there is no
  // token without asking anybody; this page used to wait on `GET /customer-auth/me` instead, so a
  // signed-out visitor watched a spinner through a 401 and three retries — measured at seven seconds —
  // where legacy sends them straight to the sign-in form. All three of this page's routes did it.
  if (sessionUser === null) {
    return <Redirect to="/prihlasenie" />
  }

  if (account.isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-profile-loading>
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    )
  }

  // **Only *not signed in* sends somebody to the sign-in form.** This redirected on any error at all,
  // and `retry` is off — so a 429 from the shared rate limiter, a 500, or one dropped connection
  // bounced a customer with a perfectly good session to a login page. They would reasonably conclude
  // they had been signed out and type their password again. `BLOCKED.md` #13 records the 429 reading
  // as a signed-out visitor inside the suite; a phone behind a carrier's NAT sees the same thing.
  //
  // A real 401 does not need this branch: `custom-fetch` calls `notifyUnauthorized()` on one, which
  // clears the store, and the `sessionUser === null` redirect above catches it.
  const signedOut =
    account.error instanceof ApiError && (account.error.status === 401 || account.error.status === 403)
  if (signedOut) {
    return <Redirect to="/prihlasenie" />
  }

  if (account.isError || user === undefined) {
    return (
      <div
        className="min-h-[60vh] flex flex-col items-center justify-center gap-3 px-6 text-center"
        data-profile-unreachable
      >
        <p className="text-sm text-muted-foreground">
          <T>Účet sa nepodarilo načítať. Skúste to prosím znova.</T>
        </p>
        <button
          type="button"
          onClick={() => void account.refetch()}
          data-profile-retry
          className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-bold"
        >
          <T>Skúsiť znova</T>
        </button>
      </div>
    )
  }

  /**
   * What a pull down the page reloads.
   *
   * Everything the screen shows, because the customer pulling is asking *has anything changed* — and an
   * order that moved to *on the way* is the likeliest answer. Legacy's `refreshAll` does the same.
   */
  /**
   * Put a past order back in the basket.
   *
   * Fetched on demand rather than with the list: the history shows ten orders and a customer reorders
   * one, so ten item fetches to save one is the wrong trade.
   */
  async function again(orderId: number) {
    setReordering(orderId)
    setReorderProblem('')
    try {
      // Both on demand, and both awaited before anything is judged off-menu: an empty catalogue
      // would read as *every dish has been withdrawn* and silently reorder nothing.
      const [detail, products] = (await Promise.all([
        getCustomerAuthMyOrdersOrderId(orderId),
        getProducts({}),
      ])) as [{ items?: unknown }, unknown]
      const items = (Array.isArray(detail.items) ? detail.items : []) as PastOrderItem[]
      const onMenu = new Set(
        (Array.isArray(products) ? products : [])
          .filter(
            (product): product is { id: number; active?: boolean } =>
              typeof product === 'object' &&
              product !== null &&
              typeof (product as { id?: unknown }).id === 'number',
          )
          .filter((product) => product.active !== false)
          .map((product) => product.id),
      )
      const reorder = reorderFrom(items, onMenu)
      for (const line of reorder.lines) addLine(line)
      setReorderProblem(reorderNote(reorder))
      if (reorder.lines.length > 0) navigate('/checkout')
    } catch {
      setReorderProblem(tt('Objednávku sa nepodarilo načítať.'))
    } finally {
      setReordering(null)
    }
  }

  /**
   * Play a game, and say what it gave.
   *
   * **A refusal is a 429 carrying `nextPlayAt`**, and the server says why it bothered: the screen counts
   * down to it, and a bare *no* leaves it counting down to nothing. `refusedUntil` reads that field
   * rather than guessing, and says *tomorrow* when it is absent rather than inventing a clock.
   *
   * The status is refetched afterwards either way — a play used up is a play the screen must stop
   * offering, whether it won or was refused.
   */
  async function play(key: string) {
    setPrize(null)
    try {
      const answer = await playGame.mutateAsync({ data: playPayload(key as GameKey) })
      const won = prizeFrom(answer)
      setPrize({
        game: key,
        note: prizeNote(won),
        headline: prizeHeadline(won),
        emoji: prizeEmoji(won),
        coupon: prizeCoupon(won),
        points: prizePointsNote(won),
        won: won?.won === true,
      })
    } catch (failure) {
      const until = refusedUntil(failure)
      // A refusal is not a result: no emoji, no headline, no code — just the reason, in the panel's
      // quiet form.
      setPrize({
        game: key,
        note: until === null ? tt('Dnešné hranie ste vyčerpali') : `${tt('Ďalšia hra')}: ${until}`,
        headline: '',
        emoji: '',
        coupon: null,
        points: '',
        won: false,
      })
    } finally {
      await games.refetch()
    }
  }

  async function refreshAll() {
    await Promise.all([
      account.refetch(),
      saved.refetch(),
      history.refetch(),
      referrals.refetch(),
      myCoupons.refetch(),
      games.refetch(),
    ])
  }

  return (
    <PullToRefresh onRefresh={refreshAll}>
    <main className="max-w-2xl mx-auto p-4 space-y-4" data-profile>
      {/* §9.220 - legacy's header. `MojProfil.tsx` opens with the customer's name in an `h1` beside
        * an avatar; AGOS dropped straight into an unlabelled form, so the first thing on the page
        * was a text box. */}
      <header className="flex items-center gap-3" data-profile-header>
        <span className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center text-lg font-bold shrink-0">
          {(displayName.trim()[0] ?? '?').toUpperCase()}
        </span>
        <span className="min-w-0">
          <h1 className="text-xl font-bold truncate">
            {displayName === '' ? tt('Zákazník') : displayName}
          </h1>
          <p className="text-xs text-muted-foreground truncate" data-account-email>
            {user?.email ?? ''}
          </p>
        </span>
        {/* §9.230 - legacy has one here as well as at the foot, `hidden sm:inline` so the label
          * drops on a narrow screen and the icon stays. AGOS had only the one past every section. */}
        <button
          type="button"
          data-sign-out-header
          onClick={() => signOut()}
          className="ml-auto shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-lg hover:bg-muted/60"
        >
          <LogOut className="w-4 h-4" />
          <span className="hidden sm:inline">
            <T>Odhlásiť</T>
          </span>
        </button>
      </header>

      {/* §9.226 - legacy's shape: a header outside the card with the action on the right, and the
        * account shown as a list until somebody asks to change it. This was an open form with two
        * unlabelled boxes and the name repeated above them, which is why the page read as a form. */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-base font-bold">
              <T>Údaje účtu</T>
            </h2>
          </div>
          {!editingAccount && (
            <button
              type="button"
              data-edit-account
              onClick={() => {
                // Seed the fields from what is loaded, so *Zrušiť* has something to go back to.
                setName(displayName)
                setPhone(displayPhone)
                setEditingAccount(true)
              }}
              className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors px-2 py-1 rounded-lg hover:bg-primary/10"
            >
              <Pencil className="w-3.5 h-3.5" />
              <T>Upraviť</T>
            </button>
          )}
        </div>

        <section className="rounded-2xl border border-border bg-card p-4 space-y-3" data-account>
          {editingAccount ? (
            <>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  <T>Meno</T>
                </span>
                <input
                  data-field="name"
                  autoComplete="name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="w-full min-h-[40px] rounded-xl border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  <T>Telefón</T>
                </span>
                <input
                  data-field="phone"
                  type="text"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  className="w-full min-h-[40px] rounded-xl border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                />
              </label>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  data-save-profile
                  disabled={patchMe.isPending}
                  onClick={() => {
                    void patchMe
                      .mutateAsync({ data: { name: name === '' ? displayName : name, phone: phone === '' ? displayPhone : phone } })
                      .then(() => account.refetch())
                      .then(() => setEditingAccount(false))
                  }}
                  className="flex-1 min-h-[40px] px-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50"
                >
                  <T>Uložiť</T>
                </button>
                <button
                  type="button"
                  data-cancel-account
                  disabled={patchMe.isPending}
                  onClick={() => {
                    setName(displayName)
                    setPhone(displayPhone)
                    setEditingAccount(false)
                  }}
                  className="min-h-[40px] px-4 rounded-xl border border-border text-muted-foreground text-sm font-semibold"
                >
                  <T>Zrušiť</T>
                </button>
              </div>
            </>
          ) : (
            /* Legacy's `<dl>`, down to the em dash where a value is missing. `data-account-name` is
             * the same hook on the same string — the customer's name as text — so the spec that
             * reads it is reading the same thing. */
            <dl className="space-y-2 text-sm">
              <div className="flex items-start gap-3" data-account-row="name">
                <dt className="w-20 shrink-0 pt-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <T>Meno</T>
                </dt>
                <dd className="flex-1 min-w-0 break-words" data-account-name>
                  {displayName === '' ? '-' : displayName}
                </dd>
              </div>
              <div className="flex items-start gap-3" data-account-row="email">
                <dt className="w-20 shrink-0 pt-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Email
                </dt>
                {/* Read-only on purpose - §9.226. `PATCH /customer-auth/me` takes name and phone;
                  * making the address editable is a new write path, not a restyle. */}
                <dd className="flex-1 min-w-0 break-all">{user?.email ?? '-'}</dd>
              </div>
              <div className="flex items-start gap-3" data-account-row="phone">
                <dt className="w-20 shrink-0 pt-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <T>Telefón</T>
                </dt>
                <dd className="flex-1 min-w-0 break-words">
                  {displayPhone === '' ? '-' : displayPhone}
                </dd>
              </div>
            </dl>
          )}

          {/* §9.231 - legacy keeps changing a password where changing a name is: inside this card,
            * below a rule, behind a link. It had a card of its own with both fields permanently on
            * screen — the third instance of the shape §9.226 and §9.228 removed, and the last.
            * Hidden while the account itself is being edited, as legacy has it: two open forms in one
            * card, one silently discarding the other's work, is worse than either. */}
          {!editingAccount && (
            <div className="pt-3 border-t border-border">
              {!passwordOpen ? (
                <button
                  type="button"
                  data-password-open
                  onClick={() => setPasswordOpen(true)}
                  className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  <Lock className="w-3.5 h-3.5" />
                  <T>Zmeniť heslo</T>
                </button>
              ) : (
            <div className="space-y-2.5" data-password>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> <T>Zmena hesla</T>
              </p>
        <input
          data-field="current-password"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
          placeholder={tt('Súčasné heslo')}
          className="w-full min-h-[40px] rounded-xl border border-border bg-card px-3 text-sm"
        />
        <input
          data-field="new-password"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          placeholder={tt('Nové heslo, aspoň 6 znakov')}
          className="w-full min-h-[40px] rounded-xl border border-border bg-card px-3 text-sm"
        />
        <button
          type="button"
          data-change-password
          // The server's own rule: `atLeast(password, 6, "Heslo musí mať aspoň 6 znakov")`.
          disabled={newPassword.length < 6 || currentPassword === '' || changePassword.isPending}
          onClick={() => {
            void changePassword
              .mutateAsync({ data: { currentPassword, newPassword } })
              .then(() => {
                setCurrentPassword('')
                setNewPassword('')
                setNotice(tt('Heslo je zmenené.'))
                toast({ title: tt('Heslo je zmenené.') })
              })
              .catch((failure: unknown) => {
                const message = apiErrorField(failure, 'error') ?? tt('Heslo sa nepodarilo zmeniť.')
                setNotice(message)
                // The server's own wording, and a toast as well as the inline note: a failure the
                // customer scrolled past is a failure they think did not happen.
                toast({ title: message, variant: 'destructive' })
              })
          }}
          className="min-h-[40px] px-3 rounded-xl border border-border text-xs font-semibold disabled:opacity-40"
        >
          <T>Zmeniť heslo</T>
        </button>
        {notice !== '' && (
          <p className="text-[11px]" data-password-notice>
            {notice}
          </p>
        )}
            </div>
              )}
            </div>
          )}
        </section>
      </div>

      {/* §9.172 - legacy's points card. This was one line of text reading `gold · 120 bodov · 1,20 €`
          in a plain bordered box, which is what the owner meant. Legacy gives the balance a card of
          its own: a round icon, the figure at `text-3xl`, the tier as a coloured pill, and the order
          count under it. Every `data-*` hook is the same one. */}
      {/* §9.246 - legacy's *Vaša trvalá zľava*, `MojProfil.tsx:1230`. Three fields published on
          `/customer-auth/me` since the port and read by nothing, so a customer with a standing 10 %
          had no way to know they had one. Only when there is one: a card reading *0 %* is worse than
          no card. */}
      {user?.personalDiscountType != null && (user.personalDiscountValue ?? 0) > 0 && (
        <section
          className="rounded-2xl border border-violet-500/30 bg-violet-500/10 p-5"
          data-personal-discount={user.personalDiscountType}
        >
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center shrink-0">
              <Star className="w-5 h-5 text-violet-500" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wide font-medium text-violet-600 dark:text-violet-300">
                <T>Vaša trvalá zľava</T>
              </p>
              <p
                className="text-3xl font-bold text-violet-600 dark:text-violet-300"
                data-personal-discount-value
              >
                {personalDiscountAmount({
                  // `CustomerProfile` is the server's wider shape; the reader wants the three
                  // fields the discount is made of.
                  id: user.id ?? 0,
                  email: user.email ?? null,
                  name: user.name ?? null,
                  phone: user.phone ?? null,
                  pointsBalance: user.pointsBalance ?? 0,
                  personalDiscountType:
                    user.personalDiscountType === 'percent' || user.personalDiscountType === 'fixed'
                      ? user.personalDiscountType
                      : null,
                  personalDiscountValue: user.personalDiscountValue,
                  personalDiscountStackable: user.personalDiscountStackable,
                })}
              </p>
              {/* The half that matters: a discount that *replaces* means a coupon on top does
                  nothing, which §9.65 already says at the checkout in these same words. */}
              <p className="text-[11px] text-muted-foreground mt-1" data-personal-discount-note>
                {user.personalDiscountStackable === true ? (
                  <T>
                    Automaticky uplatnená pri každej objednávke. Kombinuje sa s ostatnými zľavami.
                  </T>
                ) : (
                  <T>
                    Automaticky uplatnená pri každej objednávke. Nahrádza ostatné zľavy (kupóny,
                    body, akcie).
                  </T>
                )}
              </p>
            </div>
          </div>
        </section>
      )}

      <section
        className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-3"
        data-loyalty
      >
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
            <Star className="w-5 h-5 text-primary" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
              <T>Vernostné body</T>
            </p>
            <p className="text-3xl font-bold text-primary" data-loyalty-balance>
              {balanceNote(rates, points)}
            </p>
          </div>
          {/* §9.246 - the tier's own name, emoji and colour, all three published by
              `/loyalty/public-info` and all three thrown away: this printed the key, so a Gold Master
              customer read `Gold_master`. No badge at all for somebody who is not a member. */}
          {tier !== null && (
            <span
              className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-semibold border"
              style={{
                background: `${tier.color}1A`,
                color: tier.color,
                borderColor: `${tier.color}55`,
              }}
              data-loyalty-level={tier.key}
              data-loyalty-tier-name={tier.name}
            >
              {tier.emoji} {tier.name}
            </span>
          )}
        </div>

        {orderCount > 0 && (
          <p className="text-xs text-muted-foreground" data-loyalty-orders>
            <T>Celkovo</T> {formatCount(orderCount)} <T>objednávok</T>
          </p>
        )}

        {/* Legacy draws a bar; this drew a sentence. The sentence stays - it is what says how many
            orders are left - and the bar is what makes it readable at a glance. */}
        {progress !== null && (
          <div data-loyalty-progress={progress.key}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <T>Pokrok do</T> <span>{tierName(loyalty.data, progress.key)}</span>
              </span>
              <span className="text-xs font-bold tabular-nums">
                {formatCount(orderCount)}/{formatCount(progress.to)}
              </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-500 ease-out"
                style={{ width: `${progress.fraction * 100}%` }}
              />
            </div>
          </div>
        )}
        {levelNote(rates, orderCount) !== '' && (
          <p className="text-[11px] text-muted-foreground" data-loyalty-next>
            {levelNote(rates, orderCount)}
          </p>
        )}
        {redemptionNote(rates, points) !== '' ? (
          <p className="text-[11px] text-amber-500" data-loyalty-blocked>
            {redemptionNote(rates, points)}
          </p>
        ) : (
          <p className="text-[11px] text-emerald-500" data-loyalty-redeemable>
            <T>Na uplatnenie</T>: {formatCount(maxRedeemable(rates, points))} <T>bodov</T>
          </p>
        )}
      </section>

      {/* §9.246 - the owner's N5: *"hry should not be categorized, should be visible after
          vernostné body"*. Legacy has no *Hry* heading at all; it draws each game as its own card.
          Here they sit directly under the points, which is what was asked for and where legacy has
          them relative to it.

          Not lazy any more, and that is a deliberate cost: the query was behind `deferred` because
          the section was shut. A card that is always on screen has to have asked. */}
      {gameStates.length > 0 && (
      <div className="space-y-2" data-minigames>
        {gameStates.map((game) => (
          <div
            key={game.key}
            className="rounded-2xl border border-border bg-card p-4 space-y-2"
            data-game={game.key}
            data-game-state={game.state.kind}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl shrink-0" aria-hidden="true">
                {gameEmoji(game.key)}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{gameTitle(game.key)}</p>
                {/* §9.271 - the note stands down while a prize is on screen. It rendered beside a
                  * fresh win, so the first thing somebody read after winning was that they could not
                  * play; it is true and it is not the news. */}
                {shouldSaySpent(prize !== null && prize.game === game.key, game.state) && (
                  <p className="text-xs text-muted-foreground" data-game-note>
                    {gameNote(game.state)}
                  </p>
                )}
              </div>
              {/* §9.265, owner O7 - the scratch card is rubbed, not pressed. Legacy has no button on
                * it at all: a canvas that erases under the pointer and submits itself at 60 %. The
                * other game keeps its button, because legacy's *Klikni a vyhraj* is one. */}
              {mayPlay(game.state) && game.key === 'click_and_win' && (
                /* `POST /mini-game/play` awards points and mints a coupon, so it is never called on
                   render - only when this button is pressed. */
                <button
                  type="button"
                  data-game-play={game.key}
                  disabled={playGame.isPending}
                  onClick={() => void play(game.key)}
                  className="shrink-0 min-h-[36px] px-4 rounded-xl bg-primary text-primary-foreground text-xs font-bold disabled:opacity-50"
                >
                  {playGame.isPending ? <T>Hrám…</T> : <T>Hrať</T>}
                </button>
              )}
            </div>
            {mayPlay(game.state) && game.key === 'scratch_card' && (
              <ScratchCard disabled={playGame.isPending} onReveal={() => void play(game.key)} />
            )}

            {/* §9.271, owner O16 - the result as a panel rather than a caption. Legacy's shape: the
              * outcome first and large, then what it is worth, then **the code**, then what was
              * credited. Nothing new is fetched — three of these four fields were already parsed and
              * thrown away at the point of display. */}
            {prize !== null && prize.game === game.key && (
              <div
                data-game-result={game.key}
                className={cn(
                  'rounded-xl border p-3 text-center space-y-1.5',
                  prize.won
                    ? 'border-emerald-500/40 bg-emerald-500/10'
                    : 'border-border bg-muted/40',
                )}
              >
                <div className="text-3xl" aria-hidden="true">
                  {prize.emoji}
                </div>
                <p
                  className={cn(
                    'font-bold text-base',
                    prize.won ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground',
                  )}
                  data-game-headline
                >
                  {prize.headline}
                </p>
                <p className="text-sm font-semibold" data-game-prize={game.key}>
                  {prize.note}
                </p>
                {prize.coupon !== null && (
                  <div
                    data-game-coupon
                    className="mx-auto inline-block rounded-lg border-2 border-dashed border-emerald-500/60 bg-background px-4 py-2 font-mono font-bold tracking-widest text-emerald-600 dark:text-emerald-400"
                  >
                    {prize.coupon}
                  </div>
                )}
                {prize.points !== '' && (
                  <p className="text-xs text-muted-foreground" data-game-points>
                    {prize.points}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      )}

      {/* §9.228 - legacy's header, outside the card with the action on the right. */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-base font-bold">
              <T>Uložené adresy</T>
            </h2>
          </div>
          {!addingAddress && (
            <button
              type="button"
              data-address-new-open
              onClick={() => setAddingAddress(true)}
              className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors px-2 py-1 rounded-lg hover:bg-primary/10"
            >
              <Plus className="w-3.5 h-3.5" />
              <T>Pridať novú</T>
            </button>
          )}
        </div>

      <section className="rounded-2xl border border-border bg-card p-4 space-y-2" data-addresses>
        {addresses.map((entry: SavedAddress) => (
          <SavedAddressRow
            key={entry.address}
            entry={entry}
            // §9.248 — one editor open at a time, as legacy has it. `labelEditing` holds an address
            // rather than a boolean, which is also what the per-address `labelDraft` map was working
            // around when every row was editable at once.
            editing={labelEditing === entry.address}
            draft={labelDraft[entry.address] ?? entry.label ?? ''}
            saving={labelAddress.isPending}
            onEdit={(open) => setLabelEditing(open ? entry.address : null)}
            onDraft={(value) => setLabelDraft({ ...labelDraft, [entry.address]: value })}
            onSave={(payload) => {
              void labelAddress.mutateAsync({ data: payload }).then(() => {
                setLabelEditing(null)
                return saved.refetch()
              })
            }}
            onRemove={() => {
              void hideAddress
                .mutateAsync({ data: { address: entry.address } })
                .then(() => saved.refetch())
            }}
            // §9.332 - the owner: *"I cant set address to Predvolená."* `PATCH /customer-auth/me`
            // has taken `defaultAddress` all along and nothing on this screen sent it. The profile
            // is refetched as well as the list, because the default lives on the customer row and
            // `isDefault` on the list is derived from it.
            settingDefault={patchMe.isPending}
            onSetDefault={() => {
              void patchMe
                .mutateAsync({ data: defaultAddressPayload(entry.address) })
                .then(() => Promise.all([saved.refetch(), account.refetch()]))
            }}
          />
        ))}
        {/* The server collapses duplicates when it rebuilds the list, so re-saving an address already
            there is accepted and changes nothing - which reads as the button not working. `alreadyKnown`
            existed to say so and was called by nobody (FB-C58). */}
        {/* §9.228 - legacy's empty state. Without it a customer with nothing saved saw a bare text
          * box, and nothing said the sentence that matters: addresses save themselves after an
          * order, so they need do nothing. */}
        {addresses.length === 0 && !addingAddress && (
          <div
            className="rounded-2xl border border-dashed border-border p-5 text-center space-y-2.5"
            data-addresses-empty
          >
            <MapPin className="w-7 h-7 text-muted-foreground/50 mx-auto" />
            <p className="text-sm font-semibold text-foreground">
              <T>Zatiaľ žiadne uložené adresy</T>
            </p>
            <p className="text-xs text-muted-foreground">
              <T>Adresy sa automaticky ukladajú po objednávke, alebo môžete pridať novú teraz.</T>
            </p>
            <button
              type="button"
              data-address-new-open
              onClick={() => setAddingAddress(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <T>Pridať adresu</T>
            </button>
          </div>
        )}
        {addingAddress && (
        <div className="flex gap-2 border-t border-border pt-2">
          <input
            data-address-new
            value={newAddress}
            onChange={(event) => setNewAddress(event.target.value)}
            placeholder={tt('Pridať adresu')}
            className="flex-1 min-h-[36px] rounded-xl border border-border bg-card px-3 text-xs"
          />
          <button
            type="button"
            data-address-add
            disabled={newAddress.trim() === '' || alreadyKnown(addresses, newAddress)}
            onClick={() => {
              void saveAddress.mutateAsync({ data: { address: newAddress.trim() } }).then(() => {
                setNewAddress('')
                return saved.refetch()
              })
            }}
            className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold disabled:opacity-40"
          >
            <T>Pridať</T>
          </button>
        </div>
        )}
        {addingAddress && alreadyKnown(addresses, newAddress) && (
          <p className="text-[11px] text-muted-foreground" data-address-duplicate>
            <T>Túto adresu už máte uloženú.</T>
          </p>
        )}
        <button
          type="button"
          data-address-unhide-all
          onClick={() => {
            void unhideAddress.mutateAsync({ data: { address: '' } }).then(() => saved.refetch())
          }}
          className="hidden"
        >
          <T>Obnoviť</T>
        </button>
      </section>
      </div>

      <ProfileSection
        title={tt('Moje objednávky')}
        // Only once the section has been opened: a badge that asked its own query would be the
        // twelve-requests problem in a smaller shape.
        badge={isOpen('orders') && orders.orders.length > 0 ? orders.orders.length : null}
        open={isOpen('orders')}
        onToggle={() => toggle('orders')}
        data-order-history
      >
        {/* §9.229 - legacy's empty state. This was the first line alone, in `text-xs`, so the
          * section a new customer opens first said they had no orders and not what to do about it.
          * The section itself stays collapsed: §9.21 made these four lazy and legacy's open state
          * would re-eager the query that exists to avoid. */}
        {orders.orders.length === 0 && (
          <div
            className="rounded-2xl border border-dashed border-border p-6 text-center space-y-3"
            data-no-orders
          >
            <ShoppingBag className="w-8 h-8 text-muted-foreground/50 mx-auto" />
            <div>
              <p className="text-sm font-semibold text-foreground">
                <T>Zatiaľ žiadne objednávky</T>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                <T>Po prvej objednávke sa tu zobrazí história s možnosťou opätovne objednať.</T>
              </p>
            </div>
            <button
              type="button"
              data-start-ordering
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors"
            >
              <ShoppingBag className="w-4 h-4" />
              <T>Začať objednávať</T>
            </button>
          </div>
        )}
        {/* §9.396 - what the server said about the last cancel. Above the list, because the list is
          * what it is about and a notice under a fold is a notice nobody reads. */}
        {cancelNotice !== '' && (
          <p
            className="rounded-2xl border border-border bg-muted/30 p-3 text-xs"
            data-order-cancel-notice
          >
            {cancelNotice}
          </p>
        )}
        {orders.orders.map((order) => (
          <OrderHistoryCard
            key={order.id}
            order={order}
            detail={openOrders[order.id] ?? null}
            onToggle={() => void toggleOrder(order.id)}
          >
            {mayCancel(order) ? (
              confirmingCancel === order.id ? (
                <div className="w-full space-y-2" data-order-cancel-confirm={order.id}>
                  <p className="text-[11px] text-muted-foreground">
                    <strong>{cancelConfirmation().title}</strong> {cancelConfirmation().detail}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-order-cancel-yes={order.id}
                      disabled={cancelOrder.isPending}
                      onClick={() => {
                        setConfirmingCancel(null)
                        void cancelOrder
                          .mutateAsync({ orderId: order.id })
                          .then(() => {
                            setCancelNotice(cancelSucceeded())
                            return history.refetch()
                          })
                          // §9.396 - **the refusal, which reached nobody.** The server answers 409
                          // while the kitchen has the order, and the `.then` above never ran - so
                          // the list was not even reloaded and the button stayed exactly as it was.
                          .catch((failure: unknown) => {
                            setCancelNotice(cancelFailed(failure))
                            return history.refetch()
                          })
                      }}
                      className="min-h-[36px] px-3 rounded-xl bg-red-500 text-white text-xs font-bold disabled:opacity-50"
                    >
                      {cancelConfirmation().confirm}
                    </button>
                    <button
                      type="button"
                      data-order-cancel-no={order.id}
                      onClick={() => setConfirmingCancel(null)}
                      className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold"
                    >
                      <T>Nechať objednávku</T>
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  data-order-cancel={order.id}
                  onClick={() => {
                    setCancelNotice('')
                    setConfirmingCancel(order.id)
                  }}
                  className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold"
                >
                  <T>Zrušiť objednávku</T>
                </button>
              )
            ) : (
              <p className="text-[11px] text-muted-foreground w-full" data-order-cancel-note>
                {cancelNote(order)}
              </p>
            )}
            <button
              type="button"
              data-order-track={order.id}
              onClick={() => setTracked(tracked === order.id ? null : order.id)}
              className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold"
            >
              {tracked === order.id ? <T>Skryť priebeh</T> : <T>Sledovať objednávku</T>}
            </button>
            <button
              type="button"
              data-order-again={order.id}
              disabled={reordering !== null}
              onClick={() => void again(order.id)}
              className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold disabled:opacity-50"
            >
              {reordering === order.id ? <T>Pridávam…</T> : <T>Objednať znova</T>}
            </button>
            {tracked === order.id && tracking !== null && (
              <ol
                className="w-full text-[11px] text-muted-foreground space-y-0.5 pt-1"
                data-tracking={order.id}
              >
                {tracking.timeline.map((step) => (
                  <li key={`${step.status}-${step.at}`} data-tracking-step={step.status}>
                    {stepNote(step, statusLabel)}
                  </li>
                ))}
                {tracking.timeline.length === 0 && <li data-tracking-empty>{emptyTimelineNote()}</li>}
              </ol>
            )}
          </OrderHistoryCard>
        ))}

        {nextLimit(orders) !== null && (
          <button
            type="button"
            data-more-orders
            disabled={history.isFetching}
            onClick={() => setOrderLimit(nextLimit(orders) ?? orderLimit)}
            className="w-full min-h-[40px] rounded-xl border border-border text-xs font-semibold disabled:opacity-50"
          >
            {history.isFetching ? (
              <T>Načítavam…</T>
            ) : (
              <>
                <T>Načítať ďalších</T> {formatCount(ORDERS_PAGE_SIZE)}
              </>
            )}
          </button>
        )}
      </ProfileSection>

      <ProfileSection
        title={tt('Moje kupóny')}
        // §9.272 — the usable ones. A number beside *Moje kupóny* is read as *how many can I use*,
        // and it counted every coupon at every status.
        badge={isOpen('coupons') && usable(coupons).length > 0 ? usable(coupons).length : null}
        open={isOpen('coupons')}
        onToggle={() => toggle('coupons')}
        data-my-coupons
      >
        {/* §9.272, owner O17 - legacy's four filters, defaulting to the ones that can be used.
          * `MyCoupons.tsx:51` has had them since the port; AGOS listed every status together. */}
        {coupons.length > 0 && (
          <div className="flex gap-1 -mx-1 px-1 overflow-x-auto sm:flex-wrap sm:overflow-x-visible" data-coupon-filters>
            {COUPON_FILTERS.map((filter) => {
              const count = couponFilterCount(coupons, filter)
              return (
                <button
                  key={filter}
                  type="button"
                  data-coupon-filter={filter}
                  aria-pressed={couponFilter === filter}
                  onClick={() => setCouponFilter(filter)}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-xs font-bold transition-colors whitespace-nowrap shrink-0',
                    couponFilter === filter
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:text-foreground',
                  )}
                >
                  {couponFilterLabel(filter)}
                  {count > 0 && <span className="ml-1.5 opacity-70">({count})</span>}
                </button>
              )
            })}
          </div>
        )}
        {shownCoupons.length === 0 && (
          <p className="text-xs text-muted-foreground" data-no-coupons>
            {emptyNote(coupons.length > 0)}
          </p>
        )}
        {/* §9.249 - the owner's N4. This was `{code} - {worth}` over one grey run-on sentence that
            merged source, minimum and status, so the one field somebody scans for - *can I use this*
            - read the same as the two they do not. Four helpers with four meanings, joined at the
            point of display; separated here, with no new data. Legacy has no coupons screen to port
            from, so this follows the profile's own card idiom. */}
        {shownCoupons.map((coupon) => (
          <div
            key={coupon.code}
            className="rounded-xl border border-border p-3 space-y-1"
            data-coupon={coupon.code}
            data-coupon-status={coupon.status}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-bold">{worthNote(coupon)}</p>
                {/* A coupon code is a thing you type, so it is written the way every other code on
                    these screens is written. */}
                <p className="font-mono text-[11px] tracking-wider text-muted-foreground mt-0.5">
                  {coupon.code}
                </p>
              </div>
              {/* Always said, and the tone carries it: `statusNote` answers nothing for a usable
                  coupon - correctly, it names a problem - which left the one field somebody scans
                  for as the one with nothing on screen. */}
              <span
                className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                  statusIsGood(coupon)
                    ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                    : 'bg-muted text-muted-foreground'
                }`}
                data-coupon-status-note
              >
                {statusBadge(coupon)}
              </span>
            </div>
            {/* Its own line: *platí od 15 €* is the reason a coupon does not apply, and it should
                not be findable only by reading to the end of a sentence. */}
            {minimumNote(coupon) !== '' && (
              <p className="text-[11px] text-muted-foreground" data-coupon-minimum>
                {minimumNote(coupon)}
              </p>
            )}
            {sourceNote(coupon) !== '' && (
              <p className="text-[11px] text-muted-foreground" data-coupon-source>
                {sourceNote(coupon)}
              </p>
            )}
          </div>
        ))}
      </ProfileSection>


      {/* *Odporúčaj priateľa* - this customer's own code, which was written down as unavailable because
          `customer/referral` had been recorded as a duplicate of `me/referrals`. It is not: one answers
          who you referred, the other answers **your code, your link and what it is worth**. FB-C65. */}
      {offer !== null && (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 space-y-3" data-referral-offer>
          <div className="flex items-center gap-2">
            <Share2 className="w-4 h-4 text-emerald-600" />
            <h2 className="font-semibold text-emerald-800 text-sm">
              <T>Odporúčaj priateľa</T>
            </h2>
          </div>
          <p className="text-xs text-emerald-700 leading-relaxed">
            <T>Zdieľaj svoj odkaz. Priateľ dostane</T>{' '}
            <strong>
              {referralDiscountLabel(offer, formatCents)} <T>zľavu</T>
            </strong>{' '}
            <T>na prvú objednávku a ty dostaneš</T>{' '}
            <strong>
              {formatCount(offer.rewardPoints)} <T>bodov</T>
            </strong>{' '}
            <T>na účet.</T>
          </p>
          <div className="flex gap-2">
            <div
              data-referral-code
              className="flex-1 bg-white rounded-xl border border-emerald-200 px-3 py-2 text-sm font-mono text-emerald-800 truncate select-all"
            >
              {offer.code}
            </div>
            <button
              type="button"
              data-referral-copy
              onClick={() => {
                // `writeText` can be refused — an insecure origin, a denied permission — and a button
                // that silently does nothing is worse than one that says it could not.
                void navigator.clipboard
                  ?.writeText(shareText(offer))
                  .then(() => setCopiedCode(true))
                  .catch(() => setCopiedCode(false))
              }}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold whitespace-nowrap"
            >
              {copiedCode ? <CheckCheck className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copiedCode ? tt('Skopírované!') : tt('Kopírovať')}
            </button>
          </div>
        </section>
      )}

      {/* Legacy's own section. This port printed the count and nothing else, and a number cannot answer
          the question somebody opens this to ask - *did my friend order, and did I get the points* -
          because both are per-row facts. FB-C57. */}
      <ProfileSection
        title={tt('Tvoji odporučení')}
        badge={null}
        open={isOpen('referrals')}
        onToggle={() => toggle('referrals')}
        data-referrals
      >

        {hasReferrals(referralActivity) ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 space-y-3">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="text-2xl font-bold text-emerald-800" data-referral-count>
                  {formatCount(referralActivity.totalCount)}
                </p>
                <p className="text-xs text-emerald-700">
                  <T>Odporučení</T>
                </p>
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-emerald-800" data-referral-rewarded>
                  {formatCount(referralActivity.rewardedCount)}
                </p>
                <p className="text-xs text-emerald-700">
                  <T>Odmenených</T>
                </p>
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-emerald-800" data-referral-points>
                  {formatCount(referralActivity.totalPoints)}
                </p>
                <p className="text-xs text-emerald-700">
                  <T>Získaných bodov</T>
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              {referralActivity.referrals.map((entry) => (
                <div
                  key={entry.id}
                  data-referral={entry.id}
                  className="flex items-center justify-between gap-2 text-xs bg-white/60 rounded-lg px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-emerald-900 truncate">{referralName(entry)}</p>
                    <p className="text-emerald-700/80 text-[10px]">{referralDate(entry)}</p>
                  </div>
                  <span
                    data-referral-note
                    className={
                      entry.rewarded
                        ? 'text-emerald-700 font-bold whitespace-nowrap'
                        : 'text-amber-700 italic whitespace-nowrap'
                    }
                  >
                    {referralNote(entry)}
                  </span>
                </div>
              ))}

              {referralActivity.hasMore && (
                <button
                  type="button"
                  data-more-referrals
                  disabled={referrals.isFetching}
                  onClick={() => setReferralLimit(referralActivity.referrals.length + ORDERS_PAGE_SIZE)}
                  className="w-full py-2 mt-1 rounded-xl border border-dashed border-emerald-300 text-xs font-medium text-emerald-700 disabled:opacity-50"
                >
                  {referrals.isFetching ? (
                    <T>Načítavam…</T>
                  ) : (
                    <>
                      <T>Načítať ďalších</T> {formatCount(ORDERS_PAGE_SIZE)}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/50 p-5 text-center space-y-1.5" data-no-referrals>
            <p className="text-sm font-semibold text-emerald-900">
              <T>Zatiaľ žiadne odporučenia</T>
            </p>
            {/* Legacy's sentence, with its punctuation **outside** the translated string, as legacy
                has it - the full stop belongs to the referral code that sits between the two halves.
                Writing it inside made the string *…odkaz.* where legacy has *…odkaz*, which §8's own
                byte-for-byte check caught immediately. The code itself is not shown: no endpoint on
                this backend returns it, which is recorded rather than papered over. */}
            <p className="text-xs text-emerald-700/80">
              <T>Tvoji priatelia ešte nepoužili tvoj odkaz</T>.
            </p>
          </div>
        )}
      </ProfileSection>


      {/* §9.230 - legacy gives this block an `<h2>`. Without it the two most consequential controls
        * on the page, export my data and delete my account, sat under nothing at all. */}
      <section className="rounded-2xl border border-border bg-card p-4 space-y-2" data-gdpr>
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-bold">
            <T>Súkromie a údaje</T>
          </h2>
        </div>
        <a
          href="/api/v1/customer-auth/me/data-export"
          data-data-export
          className="block text-xs underline"
        >
          <T>Stiahnuť moje údaje</T>
        </a>
        {/* §9.240 - three steps, so the request and the withdrawal can never be offered together.
          * The request is not a bare press: the server stamps the date *and* switches both marketing
          * opt-ins off in the same transaction, which is more than the button says. */}
        {deletionAt === 'idle' && (
          <button
            type="button"
            data-request-deletion
            onClick={() => setConfirmingDeletion(true)}
            className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold"
          >
            <T>Požiadať o vymazanie účtu</T>
          </button>
        )}

        {deletionAt === 'confirming' && (
          <div className="space-y-2" data-deletion-confirm>
            <p className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs">
              <strong>
                <T>Naozaj zmazať účet?</T>
              </strong>{' '}
              {deletionConsequences()}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                data-deletion-yes
                disabled={requestDeletion.isPending}
                onClick={() => {
                  void requestDeletion
                    .mutateAsync()
                    .then(() => account.refetch())
                    // §9.396 - **a departure from parity, with a reason.** Legacy is silent here
                    // too (`if (r.ok) await refreshUser()`), and a request under GDPR that fails
                    // quietly is a promise the customer believes was made.
                    .catch((failure: unknown) =>
                      setNotice(
                        apiErrorField(failure, 'error') ??
                          tt('Žiadosť o zmazanie sa nepodarilo odoslať.'),
                      ),
                    )
                    .finally(() => setConfirmingDeletion(false))
                }}
                className="flex-1 min-h-[36px] rounded-xl bg-red-500 text-white text-xs font-bold disabled:opacity-50"
              >
                <T>Áno, zmazať</T>
              </button>
              <button
                type="button"
                data-deletion-no
                onClick={() => setConfirmingDeletion(false)}
                className="px-4 min-h-[36px] rounded-xl border border-border text-xs font-semibold"
              >
                <T>Zrušiť</T>
              </button>
            </div>
          </div>
        )}

        {deletionAt === 'requested' && (
          <div
            className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-1.5"
            data-deletion-requested
          >
            <p className="text-xs font-semibold">
              <T>Vyžiadané zmazanie účtu</T>
            </p>
            {/* The date has been on the wire since the port and no screen read it. Without it a
              * customer cannot tell this morning's request from June's. */}
            <p className="text-[11px]">
              {deletionRequestedNote(user?.deletionRequestedAt)} {deletionConsequences()}
            </p>
            <button
              type="button"
              data-deletion-withdraw
              disabled={withdrawDeletion.isPending}
              onClick={() => {
                void withdrawDeletion
                  .mutateAsync()
                  .then(() => account.refetch())
                  // §9.396 - the other half: a withdrawal that failed leaves the account still
                  // queued for deletion, which is the one outcome the customer must not misread.
                  .catch((failure: unknown) =>
                    setNotice(
                      apiErrorField(failure, 'error') ??
                        tt('Žiadosť sa nepodarilo zrušiť.'),
                    ),
                  )
              }}
              className="text-xs font-bold underline disabled:opacity-50"
            >
              <T>Zrušiť žiadosť</T>
            </button>
            <p className="text-[11px] text-muted-foreground">{withdrawalNote()}</p>
          </div>
        )}
      </section>

      {reorderProblem !== '' && (
        <p className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs" data-reorder-note>
          {reorderProblem}
        </p>
      )}

      <NotificationPreferences
        publicKey={
          typeof (pushKey.data as { publicKey?: unknown } | undefined)?.publicKey === 'string'
            ? (pushKey.data as { publicKey: string }).publicKey
            : null
        }
        hasSubscription={push.state === 'granted-active'}
        pushStateOverride={push.state}
        pushBusy={push.busy}
        pushProblem={push.problem}
        onPush={(enable) => void (enable ? push.enable() : push.disable())}
        marketingEmail={user?.marketingEmailOptIn !== false}
        marketingSms={user?.marketingSmsOptIn !== false}
        saving={patchMe.isPending}
        onChange={(field, value) => {
          void patchMe
            .mutateAsync({ data: { [field]: value } })
            .then(() => account.refetch())
            .then(() => setNotice(tt('Uložené.')))
            // §9.395 - the server's own words, then the fixed sentence. The password change forty
            // lines up already reads them; this neighbour did not.
            // §9.395 - the server's own words, then the fixed sentence. The password change forty
            // lines up already reads them; this neighbour did not.
            .catch((failure: unknown) =>
              setNotice(
                apiErrorField(failure, 'error') ?? tt('Nastavenie sa nepodarilo uložiť.'),
              ),
            )
        }}
      />

      <button
        type="button"
        data-sign-out
        onClick={() => signOut()}
        className="w-full min-h-[44px] rounded-xl border border-border text-sm font-semibold"
      >
        <T>Odhlásiť sa</T>
      </button>
    </main>
    </PullToRefresh>
  )
}
